/**
 * ロールアウト詳細 `/updates/rollouts/$rolloutId`。
 *
 * 「止める」「戻す」を画面上部に常時置く。障害時にスクロールを探させない。
 * ロールバックは取り返しがつかないので `<ConfirmTextDialog>`（版名の完全一致入力）を通す。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronsRight, PauseCircle, Play, Undo2 } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  DataTable,
  KeyValue,
  PageHeader,
  Section,
  StatusBadge,
  formatCount,
  formatDateTime,
  type DataTableColumn,
} from '@mail-sv/ui';
import { labelOf } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { usePermission } from '../stores/auth.js';
import { ConfirmTextDialog } from '../components/dialogs.js';
import { DetailSkeleton, QueryError, RefetchingDot } from '../components/states.js';
import { RingProgress } from './updates.js';
import { RID } from './ids.js';
import type { RolloutDetail, RolloutTarget } from '../types/api.js';

const routeApi = getRouteApi(RID.rolloutDetail);

export function RolloutDetailPage(): React.ReactElement {
  const { rolloutId } = routeApi.useParams();
  const qc = useQueryClient();
  const canPublish = usePermission(OPS_PERMISSION.updatePublish);
  const canHalt = usePermission(OPS_PERMISSION.updateHalt);
  const [rollbackOpen, setRollbackOpen] = React.useState(false);

  const query = useQuery({
    queryKey: qk.rollout(rolloutId),
    queryFn: () => api.get<RolloutDetail>(`/updates/rollouts/${rolloutId}`),
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 5_000 : false),
  });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: qk.rollout(rolloutId) });
    void qc.invalidateQueries({ queryKey: qk.rollouts() });
  };

  const advance = useMutation({
    mutationFn: () => api.post(`/updates/rollouts/${rolloutId}/advance`, {}),
    onSuccess: invalidate,
  });
  const halt = useMutation({
    mutationFn: () => api.post(`/updates/rollouts/${rolloutId}/halt`, {}),
    onSuccess: invalidate,
  });
  const rollback = useMutation({
    mutationFn: () => api.post(`/updates/rollouts/${rolloutId}/rollback`, {}),
    onSuccess: () => {
      invalidate();
      setRollbackOpen(false);
    },
  });

  const rollout = query.data;
  const running = rollout?.status === 'running';
  /*
   * **draft を開始できる導線をここに置く。**
   *
   * `POST /ops/updates/rollouts` は `status='draft'` で作るだけで、
   * running にするのは `advance` だけ（platform.ts の advance ハンドラが
   * `status:'running'` を書いて `rollout.tick` を積む）。以前は
   * 「次のリングへ進める」を `running` のときしか出していなかったので、
   * **作ったロールアウトを画面から一度も開始できなかった**。
   * halted / paused からの再開も同じ `advance` なので、状態で文言だけ変える。
   */
  const canStart = rollout ? rollout.status === 'draft' : false;
  const canResume = rollout ? rollout.status === 'paused' || rollout.status === 'halted' : false;
  const finished = rollout ? rollout.status === 'completed' || rollout.status === 'rolled_back' : true;
  const advanceLabel = canStart ? '配信を開始' : canResume ? '再開する' : '次のリングへ進める';

  return (
    <>
      <PageHeader
        title={rollout ? `${rollout.component} ${rollout.version}` : 'ロールアウト'}
        description={rollout ? `開始者: ${rollout.startedBy} / ${formatDateTime(rollout.startedAt)}` : undefined}
        breadcrumbs={[
          { label: 'アップデート配信', href: '/updates' },
          { label: rollout ? `${rollout.component} ${rollout.version}` : '…' },
        ]}
        actions={
          <>
            <RefetchingDot active={query.isFetching && !query.isLoading} />
            {rollout ? <StatusBadge status={rollout.status} dot pulse={running} /> : null}
            {canHalt && running ? (
              <Button variant="outline" size="sm" onClick={() => halt.mutate()} loading={halt.isPending}>
                <PauseCircle aria-hidden />
                一時停止
              </Button>
            ) : null}
            {canPublish && rollout && !finished ? (
              <Button
                variant={canStart ? 'default' : 'outline'}
                size="sm"
                onClick={() => advance.mutate()}
                loading={advance.isPending}
              >
                {canStart ? <Play aria-hidden /> : <ChevronsRight aria-hidden />}
                {advanceLabel}
              </Button>
            ) : null}
            {canHalt && rollout && !finished ? (
              <Button variant="destructive" size="sm" onClick={() => setRollbackOpen(true)}>
                <Undo2 aria-hidden />
                ロールバック
              </Button>
            ) : null}
          </>
        }
      />

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}
      {query.isLoading ? <DetailSkeleton /> : null}

      {rollout ? (
        <div className="space-y-6">
          {rollout.status === 'draft' ? (
            /*
             * **作っただけの状態を明示する。** draft は 1 台にも配っていないのに
             * 一覧では他の行と同じ見た目で並ぶので、「配信済み」と誤読される。
             */
            <Alert>
              <AlertTitle>まだ配信していません（下書き）</AlertTitle>
              <AlertDescription>
                「配信を開始」を押すと canary から順に配り始めます。押すまでどのノードにも適用されません。
              </AlertDescription>
            </Alert>
          ) : null}

          {rollout.status === 'halted' ? (
            <Alert variant="destructive">
              <AlertTitle>ロールアウトが停止しています</AlertTitle>
              <AlertDescription>
                {/* 停止は「ヘルスゲート超過による自動」と「運営者が押した手動」の
                    両方がある。理由は API がそのまま返すのでそれを出す。 */}
                {rollout.haltReason ?? 'ヘルスゲートの閾値を超えたため停止しています。'}
                <span className="mt-1 block">
                  失敗したノードは下の一覧で <code className="font-mono">failed</code> として表示されます。原因を潰してから「再開する」を押してください。
                </span>
              </AlertDescription>
            </Alert>
          ) : null}

          <Section title="進行状況">
            <div className="space-y-4 rounded-lg border border-border bg-card p-4">
              <RingProgress rings={rollout.rings} />
              <KeyValue
                columns={3}
                items={rollout.rings.map((r) => ({
                  key: labelOf(r.ring),
                  value: (
                    <span className="text-sm">
                      成功 {formatCount(r.succeeded)} / 失敗 {formatCount(r.failed)} / 残り {formatCount(r.pending)}
                      <span className="block text-xs text-muted-foreground">
                        soak {r.soakMinutes} 分 / 開始 {formatDateTime(r.startedAt)}
                      </span>
                    </span>
                  ),
                }))}
              />
            </div>
          </Section>

          <Section title="適用条件" description="このロールアウトが従う制約。">
            <div className="rounded-lg border border-border bg-card p-4">
              <KeyValue
                columns={3}
                items={[
                  { key: '同時停止の上限', value: `${rollout.maxUnavailablePct}%` },
                  { key: 'いま配っているリング', value: rollout.currentRing ? labelOf(rollout.currentRing) : '—' },
                  {
                    key: '適用してよい時間帯',
                    value: <MaintenanceWindowValue window={rollout.maintenanceWindow} />,
                  },
                ]}
              />
            </div>
          </Section>

          <Section title="ヘルスゲート" description="soak 中にこの条件を満たさなければ自動停止します。">
            {rollout.healthGates.length === 0 ? (
              <p className="text-sm text-muted-foreground">ヘルスゲートは設定されていません。</p>
            ) : (
              <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
                {rollout.healthGates.map((g) => (
                  <div key={g.name} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
                    <span className="w-48 shrink-0 font-medium">{g.name}</span>
                    <span className="text-xs text-muted-foreground">閾値 {g.threshold}</span>
                    <span className="text-xs tabular">実測 {g.observed ?? '—'}</span>
                    {g.ok === null ? (
                      <Badge variant="muted" size="sm">
                        判定前
                      </Badge>
                    ) : (
                      <StatusBadge status={g.ok ? 'succeeded' : 'failed'} size="sm" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title="対象ノード">
            <TargetTable targets={rollout.targets} />
          </Section>
        </div>
      ) : null}

      {rollout ? (
        <ConfirmTextDialog
          open={rollbackOpen}
          onOpenChange={setRollbackOpen}
          title="ロールバックする"
          description="適用済みのノードを前バージョンの desired-state へ差し戻します。配信中のリングは即座に停止します。"
          confirmText={rollout.version}
          confirmLabel="ロールバックする"
          pending={rollback.isPending}
          error={rollback.isError ? rollback.error : undefined}
          onConfirm={() => rollback.mutate()}
        />
      ) : null}
    </>
  );
}

/**
 * 適用してよい時間帯（`{tzid, cron, durationMin}`）の表示。
 *
 * **設定する口がまだ無い。** `POST /ops/updates/rollouts` の body に
 * `maintenanceWindow` が無いので、いまはどのロールアウトも空（＝時間帯の制約
 * なし）になる。空欄を「—」とだけ書くと「設定し忘れ」に見えるので、
 * 「常時適用」と読める形で出し、設定手段が無いことも添える（R82）。
 */
function MaintenanceWindowValue({
  window: w,
}: {
  window: RolloutDetail['maintenanceWindow'];
}): React.ReactElement {
  if (!w || !w.cron) {
    return (
      <span className="text-sm">
        常時適用
        <span className="block text-xs text-muted-foreground">時間帯の指定は現在 API 側に入力口がありません。</span>
      </span>
    );
  }
  return (
    <span className="text-sm">
      <span className="font-mono text-xs">{w.cron}</span>
      <span className="block text-xs text-muted-foreground">
        {w.tzid ?? 'UTC'} / {w.durationMin ?? 0} 分間
      </span>
    </span>
  );
}

function TargetTable({ targets }: { targets: RolloutTarget[] }): React.ReactElement {
  const columns: Array<DataTableColumn<RolloutTarget>> = [
    {
      id: 'hostname',
      header: 'ノード',
      cell: (t) => (
        <Link
          to="/nodes/$nodeId"
          params={{ nodeId: t.nodeId }}
          search={{ tab: 'updates', range: '24h' }}
          className="font-medium hover:text-primary"
        >
          {t.hostname}
        </Link>
      ),
    },
    { id: 'ring', header: 'リング', cell: (t) => labelOf(t.ring) },
    { id: 'status', header: '状態', cell: (t) => <StatusBadge status={t.status} size="sm" dot /> },
    {
      id: 'version',
      header: 'バージョン',
      hideBelow: 'md',
      cell: (t) => (
        <span className="tabular text-xs">
          {t.fromVersion ?? '—'} → {t.toVersion}
        </span>
      ),
    },
    {
      id: 'health',
      header: 'ヘルス',
      hideBelow: 'lg',
      cell: (t) =>
        t.healthGateOk === null ? '—' : <StatusBadge status={t.healthGateOk ? 'succeeded' : 'failed'} size="sm" />,
    },
    { id: 'finished', header: '完了', hideBelow: 'lg', cell: (t) => formatDateTime(t.finishedAt) },
    {
      id: 'message',
      header: '備考',
      cell: (t) => <span className="text-xs text-muted-foreground">{t.message ?? '—'}</span>,
    },
  ];

  return (
    <DataTable columns={columns} rows={targets} rowKey={(t) => t.nodeId} emptyTitle="対象ノードがありません" />
  );
}
